import { Link } from 'react-router-dom';
import { company } from '../data/company';
import { categories } from '../data/categories';
import { useReveal } from '../hooks/useReveal';
import PageHero from '../components/PageHero';
import SectionHeading from '../components/SectionHeading';
import CTASection from '../components/CTASection';
import Icon, { type IconName } from '../components/Icon';
import { LogoMark } from '../components/brand/Logo';
import cncMachining from '../assets/images/cnc-machining.jpg';
import refineryPlant from '../assets/images/refinery-plant.jpg';

const values: { icon: IconName; title: string; description: string }[] = [
  {
    icon: 'shield',
    title: 'Consistent Quality',
    description: 'Rigorous quality control and ASTM-tested specifications on every batch we ship.',
  },
  {
    icon: 'flask',
    title: 'Technical Expertise',
    description: 'Decades of petroleum-refining chemistry knowledge behind every formulation.',
  },
  {
    icon: 'globe',
    title: 'Reliable Supply',
    description: 'Dependable production planning and supply-chain management for uninterrupted delivery.',
  },
  {
    icon: 'handshake',
    title: 'Customer Partnership',
    description: 'Hands-on support and formulator flexibility to help you succeed.',
  },
];

const milestones = [
  { year: '1986', text: 'Founded in Ankleshwar, Gujarat, with a focus on petroleum refining chemistry.' },
  { year: 'Growth', text: 'Expanded into sodium & calcium petroleum sulphonates as a cornerstone offering.' },
  { year: 'Today', text: 'Four product families serving metalworking, oilfield, mining, textile and fuel markets.' },
  { year: 'Ahead', text: 'Continued investment in quality, technical support and reliable global supply.' },
];

export default function About() {
  useReveal('about');

  return (
    <div>
      <PageHero
        eyebrow="About Us"
        title="Your trusted partner in petrochemicals"
        description={`${company.legalName} has been committed to delivering high-quality petroleum-based chemicals from our base in Ankleshwar, Gujarat — ${company.since.toLowerCase()}.`}
        image={cncMachining}
      />

      {/* Story */}
      <section className="container-page py-16 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="reveal">
            <p className="eyebrow">Our Story</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-text-primary">
              High-quality chemistry, dependable service
            </h2>
            <p className="mt-5 leading-7 text-text-secondary">
              Since our inception, {company.legalName} has been committed to delivering high-quality
              petroleum-based chemicals from our base in Ankleshwar, Gujarat, India. Our goal is to
              provide customers with premium products, advanced technical expertise and dependable
              service — ensuring exceptional value in the industry.
            </p>
            <p className="mt-4 leading-7 text-text-secondary">
              Our petroleum sulphonates, including sodium and neutral calcium sulphonates, along with
              metalworking fluids, are a cornerstone of our offerings — developed with decades of
              experience in petroleum refining. The superior emulsion stability, anticorrosion
              properties and predictable performance of our products are reliably supplied by a name
              you can trust.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/products" className="btn-primary">
                Our Products <Icon name="arrow" className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="btn-ghost">
                Get in touch
              </Link>
            </div>
          </div>

          <div className="reveal relative">
            <div className="relative overflow-hidden rounded-lg bg-primary-deep p-10 text-white">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${refineryPlant})` }}
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-deep/90 via-primary-deep/55 to-primary-deep/20" aria-hidden="true" />
              <div className="relative">
                <LogoMark className="h-14 w-14" />
              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.22em] text-secondary-light">
                Headquarters
              </p>
              <p className="mt-2 text-xl font-medium leading-snug">{company.address.full}</p>
              <div className="mt-8 grid grid-cols-2 gap-6 border-t border-white/15 pt-6">
                <div>
                  <div className="text-3xl font-medium text-secondary-light">1986</div>
                  <div className="mt-1 text-xs uppercase tracking-wider text-white/60">Established</div>
                </div>
                <div>
                  <div className="text-3xl font-medium text-secondary-light">Four</div>
                  <div className="mt-1 text-xs uppercase tracking-wider text-white/60">Product families</div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-surface py-16 sm:py-20">
        <div className="container-page">
          <SectionHeading index="—" kicker="What Drives Us" title="Built on quality and reliability" />
          <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <div
                key={v.title}
                className="reveal border-t border-ink/15 pt-6"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="flex items-center justify-between">
                  <span className="index-num">{String(i + 1).padStart(2, '0')}</span>
                  <Icon name={v.icon} className="h-6 w-6 text-primary/70" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-text-primary">{v.title}</h3>
                <p className="mt-2 text-sm leading-6 text-text-secondary">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className="container-page py-16 sm:py-20">
        <SectionHeading index="—" kicker="Our Journey" title="Excellence, since 1986" />
        <div className="mx-auto mt-14 max-w-3xl">
          <ol className="relative border-l-2 border-primary/15 pl-8">
            {milestones.map((m, i) => (
              <li
                key={m.year}
                className="reveal relative pb-10 last:pb-0"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <span className="absolute -left-[41px] flex h-6 w-6 items-center justify-center rounded-full bg-primary ring-4 ring-background">
                  <span className="h-2 w-2 rounded-full bg-secondary-light" />
                </span>
                <div className="text-lg font-semibold text-text-primary">{m.year}</div>
                <p className="mt-1 leading-7 text-text-secondary">{m.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Product families quicklinks */}
      <section className="bg-surface py-16 sm:py-20">
        <div className="container-page">
          <SectionHeading index="—" kicker="Our Portfolio" title="Explore what we make" />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/products/${c.slug}`}
                className="group flex items-center gap-3 rounded-lg border border-ink/10 bg-surface p-5 transition-colors hover:border-primary/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/8 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                  <Icon name={c.icon as IconName} className="h-6 w-6" />
                </span>
                <span className="text-sm font-semibold text-text-primary">{c.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
